/**
 * TaxSetu AI - Contact Us Page
 * =============================
 * Required for Razorpay verification
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Send, Loader2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE_URL } from "../utils/api";

const API_URL = API_BASE_URL;

export default function ContactPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Message sent successfully! We\'ll get back to you soon.');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Header */}
      <header className="bg-[#0f2744] border-b border-[#1e3a5f] px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-white">
            Tax<span className="text-[#38bdf8]">Setu</span> AI
          </h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-8">
          <MessageSquare className="w-8 h-8 text-[#38bdf8]" />
          <h1 className="text-3xl font-bold text-white">Contact Us</h1>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Information */}
          <div className="bg-[#0f2744] rounded-xl p-8 border border-[#1e3a5f]">
            <h2 className="text-xl font-semibold text-white mb-6">Get in Touch</h2>
            
            <p className="text-gray-300 mb-8">
              Have questions about TaxSetu AI? We're here to help. Reach out to us through 
              any of the following channels.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#38bdf8]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#38bdf8]" />
                </div>
                <div>
                  <p className="text-white font-medium">Email</p>
                  <p className="text-gray-400">support@taxsetu.ai</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#38bdf8]/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#38bdf8]" />
                </div>
                <div>
                  <p className="text-white font-medium">Phone</p>
                  <p className="text-gray-400">+91 9711162342</p>
                  <p className="text-gray-500 text-sm">Mon-Fri, 10 AM - 6 PM IST</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#38bdf8]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#38bdf8]" />
                </div>
                <div>
                  <p className="text-white font-medium">Address</p>
                  <p className="text-gray-400">Credence Tax Advisors (OPC) Pvt. Ltd.</p>
                  <p className="text-gray-400">A-50, Street-3, New Modern Shahdara</p>
                  <p className="text-gray-400">Delhi-110032, India</p>
                </div>
              </div>
            </div>
            
            <div className="mt-8 p-4 bg-[#1a3654] rounded-lg">
              <p className="text-sm text-gray-400">
                <span className="text-white font-medium">Response Time:</span> We typically respond 
                within 24-48 hours during business days.
              </p>
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="bg-[#0f2744] rounded-xl p-8 border border-[#1e3a5f]">
            <h2 className="text-xl font-semibold text-white mb-6">Send us a Message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-[#1a3654] border border-[#2a4a6a] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#38bdf8]"
                  placeholder="Your name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full bg-[#1a3654] border border-[#2a4a6a] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#38bdf8]"
                  placeholder="your@email.com"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Subject *</label>
                <select
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  className="w-full bg-[#1a3654] border border-[#2a4a6a] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#38bdf8]"
                  required
                >
                  <option value="">Select a subject</option>
                  <option value="general">General Inquiry</option>
                  <option value="support">Technical Support</option>
                  <option value="billing">Billing & Payments</option>
                  <option value="refund">Refund Request</option>
                  <option value="feedback">Feedback</option>
                  <option value="partnership">Business Partnership</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Message *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full bg-[#1a3654] border border-[#2a4a6a] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#38bdf8] min-h-[120px] resize-none"
                  placeholder="How can we help you?"
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#38bdf8] hover:bg-[#0ea5e9] text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
